import React from 'react';

import './NumberStepper.scss';

interface NumberStepperProps {
  /** Undefined or null renders an empty field, as react-numeric-input did. */
  value: number | null | undefined;
  min?: number;
  max?: number;
  step?: number;
  /** Called with the new number, matching react-numeric-input's signature. */
  onChange: (value: number | null) => void;
  /**
   * Passed straight through. Both settings modals use this to preventDefault,
   * which is how typing into the field is blocked: the value only moves by the
   * plus and minus buttons.
   */
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * A plus/minus number field, replacing react-numeric-input.
 *
 * Deliberately matches what it replaces pixel for pixel: a 120px field with
 * the two buttons overlaid inside its edges. The one difference is that the
 * buttons are real <button> elements rather than <b> tags, so they can be
 * reached by keyboard and are announced to a screen reader.
 */
const NumberStepper = ({
  value,
  min,
  max,
  step = 1,
  onChange,
  onKeyPress,
  disabled,
  'aria-label': ariaLabel,
}: NumberStepperProps) => {
  const clamp = (next: number) => {
    let result = next;
    if (typeof min === 'number' && result < min) result = min;
    if (typeof max === 'number' && result > max) result = max;
    return result;
  };

  const hasValue = typeof value === 'number' && !Number.isNaN(value);
  // react-numeric-input left the field blank when it had no number to show, and
  // a user record that predates the bump columns still arrives without them, so
  // an empty field has to stay empty rather than read as a stored zero.
  const display = hasValue ? String(value) : '';
  const current = hasValue ? (value as number) : 0;
  const atMin = hasValue && typeof min === 'number' && current <= min;
  const atMax = hasValue && typeof max === 'number' && current >= max;

  const nudge = (direction: number) => () => {
    const next = clamp(current + direction * step);
    // From an empty field there is no current value to compare against, so the
    // first press always reports one, the way the old widget did.
    if (!hasValue || next !== current) onChange(next);
  };

  // react-numeric-input reported null for an empty field, and both callers
  // already fold null to zero, so keep that contract.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      onChange(null);
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return;
    onChange(clamp(parsed));
  };

  return (
    <span className="jk-number-stepper">
      <input
        className="jk-number-stepper__input"
        type="text"
        inputMode="numeric"
        value={display}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={handleChange}
        onKeyPress={onKeyPress}
      />
      <button
        type="button"
        className="jk-number-stepper__button jk-number-stepper__button--down"
        onClick={nudge(-1)}
        disabled={disabled || atMin}
        aria-label="Decrease"
      >
        <span className="jk-number-stepper__glyph jk-number-stepper__glyph--minus" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="jk-number-stepper__button jk-number-stepper__button--up"
        onClick={nudge(1)}
        disabled={disabled || atMax}
        aria-label="Increase"
      >
        <span className="jk-number-stepper__glyph jk-number-stepper__glyph--plus" aria-hidden="true" />
      </button>
    </span>
  );
};

export default NumberStepper;
